import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { api, type CrossPlan, type YutaiItem } from "../lib/api.js";
import { data, restrictionLevel, type RestrictionEntry } from "../lib/data.js";
import { useSession } from "../lib/auth.js";
import { yen } from "../components/ui.js";

const todayISO = () => new Date().toISOString().slice(0, 10);

export function HomePage() {
  const { data: session, isPending } = useSession();
  const [plans, setPlans] = useState<CrossPlan[]>([]);
  const [items, setItems] = useState<YutaiItem[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [restrictions, setRestrictions] = useState<Record<string, RestrictionEntry[]>>({});

  useEffect(() => {
    data.restrictions().then(setRestrictions);
    if (!session) return;
    api.crossPlans().then((r) => setPlans(r.plans)).catch(() => {});
    api.yutaiItems(false).then((r) => setItems(r.items)).catch(() => {});
    api.favorites().then((r) => setFavorites(r.favorites.map((f) => f.stockCode))).catch(() => {});
  }, [session]);

  const today = todayISO();
  const nextDay = useMemo(() => {
    const future = plans.filter((p) => p.status === "planned").map((p) => p.lastTradingDay).sort();
    return future[0] ?? null;
  }, [plans]);
  const nextPlans = plans.filter((p) => p.lastTradingDay === nextDay);
  const closeReminders = plans.filter((p) => p.needsCloseReminder);
  const expiring = items.filter((i) => i.state !== "used" && i.expiresOn && daysBetween(today, i.expiresOn) <= 30);
  const waiting = items.filter((i) => i.state === "waiting").length;
  const watched = [...new Set([...favorites, ...plans.filter((p) => p.status === "planned").map((p) => p.stockCode)])];
  const flagged = watched.map((c) => ({ code: c, level: restrictionLevel(restrictions[c]) })).filter((x) => x.level);

  if (isPending) return <p className="muted">読み込み中…</p>;
  if (!session) {
    return (
      <section>
        <h1>優待クロストラッカー</h1>
        <p>優待クロスの銘柄・コスト・届いた優待をまとめて管理します。</p>
        <Link className="btn primary" to="/login">
          ログイン / 新規登録
        </Link>
        <p className="muted">ログインしなくても「検索」から銘柄情報は見られます。</p>
      </section>
    );
  }

  return (
    <section>
      <h1>ホーム</h1>
      {closeReminders.length > 0 && (
        <div className="card highlight">
          <h2>現渡しが未記録</h2>
          <ul className="list">
            {closeReminders.map((p) => (
              <li key={p.id}>
                <Link to={`/cross/${p.id}`}>
                  {p.stockCode} {p.stockName ?? ""} / 現渡予定 {p.closeDate}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="card">
        <h2>次の権利付最終日 {nextDay ?? ""}</h2>
        {nextDay ? (
          <p>
            {nextPlans.length}件 / 予想コスト {yen(nextPlans.reduce((s, p) => s + p.estTotal, 0))} / 必要資金 {yen(nextPlans.reduce((s, p) => s + p.priceAtPlan * p.shares, 0))}
          </p>
        ) : (
          <p className="muted">予定はありません。</p>
        )}
        <Link to="/cross">クロス一覧へ</Link>
      </div>
      <div className="card">
        <h2>期限が近い優待（30日以内）</h2>
        {expiring.length === 0 ? (
          <p className="muted">ありません。到着待ち {waiting} 件。</p>
        ) : (
          <ul className="list">
            {expiring.map((i) => (
              <li key={i.id}>
                {i.stockCode} {i.stockName ?? ""} / {i.description} / 期限 {i.expiresOn}
              </li>
            ))}
          </ul>
        )}
        <Link to="/yutai">優待一覧へ</Link>
      </div>
      {flagged.length > 0 && (
        <div className="card">
          <h2>制限情報のある銘柄</h2>
          <ul className="list">
            {flagged.map((f) => (
              <li key={f.code}>
                <Link to={`/stocks/${f.code}`}>
                  {f.code} <span className={`badge ${f.level}`}>{f.level === "stop" ? "停止" : "注意"}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  return Math.round((Date.UTC(by!, bm! - 1, bd!) - Date.UTC(ay!, am! - 1, ad!)) / 86_400_000);
}
